// src/config/routes.js
import { lazy } from 'react';
import { 
  Home, 
  ShoppingCart, 
  UserCircle, 
  LogIn
} from 'lucide-react';
import { 
  mdiEiffelTower, 
  mdiBridge, 
  mdiFerrisWheel, 
  mdiBankOutline, 
} from '@mdi/js';


// Lazy load all components
const Landing = lazy(() => import('../pages/landingpage'));
const ParisLanding = lazy(() => import('../pages/paris_landing'));
const RomeLanding = lazy(() => import('../pages/rome_landing'));
const RomeLanding2 = lazy(() => import('../pages/rome_landing_b'));
const ParisLanding2 = lazy(() => import('../pages/paris_landing_b'));
const ItalyLanding = lazy(() => import('../pages/italy_landing'));
const AuthComponent = lazy(() => import('../components/AuthComponent'));
const Checkout = lazy(() => import('../components/Checkout'));
const CheckoutSuccess = lazy(() => import('../components/CheckoutSuccess'));
const UserProfile = lazy(() => import('../pages/profile'));
const ProfileTest = lazy(() => import('../pages/profileTest'));
const Translator = lazy(() => import('../pages/translator'));

// Paris Pages
const Paris = lazy(() => import('../pages/paris/paris'));
const ParisArchitecture = lazy(() => import('../pages/paris/architecture'));
const ParisPack = lazy(() => import('../pages/paris/parispack'));
const ParisStay = lazy(() => import('../pages/paris/stays'));
const EmergencyInfo = lazy(() => import('../pages/paris/emergencyinfo'));
const Metro = lazy(() => import('../pages/paris/metro'));
const Monuments = lazy(() => import('../pages/paris/monuments'));
const ParisMap = lazy(() => import('../pages/paris/parismap'));
const Museums = lazy(() => import('../pages/paris/museums'));
const ParisNightlife = lazy(() => import('../pages/paris/parisnightlife'));
const OdysseusPage = lazy(() => import('../pages/paris/odyessus'));
const LiveUpdates = lazy(() => import('../pages/paris/live'));
const ThreeStory = lazy(() => import('../pages/paris/parisstory'));
const RestaurantPage = lazy(() => import('../pages/paris/dining'));
const Bakeries = lazy(() => import('../pages/paris/boulangeries'));
const CulturalEtiquette = lazy(() => import('../pages/paris/culture'));
const FamilyCorner = lazy(() => import('../pages/paris/familycorner'));
const GreenSpaces = lazy(() => import('../pages/paris/greenspaces'));
const ShoppingDistricts = lazy(() => import('../pages/paris/shopping'));
const ParisBudget = lazy(() => import('../pages/paris/parisbudget'));
const ParisHiddenGems = lazy(() => import('../pages/paris/parishiddengems'));
const ParisItineraryBuilder = lazy(() => import('../pages/paris/itinerarybuilder'));



// Italy
const Italy = lazy(() => import('../pages/italy/italy'));
const ItalySaftey = lazy(() => import('../pages/italy/emergencyinfo'));
const TravelingInItaly = lazy(() => import('../pages/italy/italytraveling'));
const ItalianCafeGuide = lazy(() => import('../pages/italy/cafe'));
const Rome = lazy(() => import('../pages/italy/rome'));
const RomeMap = lazy(() => import('../pages/italy/romemap'));
const RomePack = lazy(() => import('../pages/italy/romepack'));
const RomeStay = lazy(() => import('../pages/italy/romestays'));
const RomeAttractions = lazy(() => import('../pages/italy/romeattractions'));
const RomeModern = lazy(() => import('../pages/italy/romemodernsites'));
const RomeNightLife = lazy(() => import('../pages/italy/romenightlife'));
const RomeJubilee = lazy(() => import('../pages/italy/romejubilee'));
const RomeOdysseusChat = lazy(() => import('../pages/italy/romeodysseus'));
const RomeStory = lazy(() => import('../pages/italy/romestory'));
const RomeLive = lazy(() => import('../pages/italy/romelive'));
const RomeFood = lazy(() => import('../pages/italy/romefood'));
const RomeRestruants = lazy(() => import('../pages/italy/romedining'));
const RomePizza = lazy(() => import('../pages/italy/romepizza'));
const RomeBudget = lazy(() => import('../pages/italy/romebudget'));
const RomeHiddenGems = lazy(() => import('../pages/italy/romehiddengems'));
const RomeFamilyCorner = lazy(() => import('../pages/italy/romefamilycorner'));
const RomeShopping = lazy(() => import('../pages/italy/romeshopping'));
const Florence = lazy(() => import('../pages/italy/florence'));
const FlorenceMap = lazy(() => import('../pages/italy/florencemap'));
const FlorenceAttractions = lazy(() => import('../pages/italy/florenceattractions'));
const FlorenceNightLife = lazy(() => import('../pages/italy/florencenightlife'));
const FlorenceOdysseusChat = lazy(() => import('../pages/italy/florenceodysseus'));
const FlorenceLive = lazy(() => import('../pages/italy/florencelive'));
const FlorenceStory = lazy(() => import('../pages/italy/florencestory'));
const FlorenceHiddenGems = lazy(() => import('../pages/italy/florencehiddengems'));
const FlorenceBudget = lazy(() => import('../pages/italy/florencebudget'));
const FlorenceFamily = lazy(() => import('../pages/italy/florencefamilycorner'));
const FlorenceSteak = lazy(() => import('../pages/italy/florencesteak'));
const FlorenceShopping = lazy(() => import('../pages/italy/florenceshopping'));
const Milan = lazy(() => import('../pages/italy/milan'));
const MilanMap = lazy(() => import('../pages/italy/milanmap'));
const MilanAttractions = lazy(() => import('../pages/italy/milanattractions'));
const MilanFood = lazy(() => import('../pages/italy/milanfood'));
const MilanNightLife = lazy(() => import('../pages/italy/milannightlife'));
const MilanOdysseusChat = lazy(() => import('../pages/italy/milanodysseus'));
const MilanLive = lazy(() => import('../pages/italy/milanlive'));
const MilanStory = lazy(() => import('../pages/italy/milanstory'));
const MilanHiddenGems = lazy(() => import('../pages/italy/milanhiddengems'));
const MilanBudget = lazy(() => import('../pages/italy/milanbudget'));
const MilanShopping = lazy(() => import('../pages/italy/milanshopping'));
const NaplesMap = lazy(() => import('../pages/italy/naplesmap'));
const VeniceMap = lazy(() => import('../pages/italy/venicemap'));

// London
const London = lazy(() => import('../pages/london/london'));
const LondonMonuments = lazy(() => import('../pages/london/monuments'));
const LondonAttractions = lazy(() => import('../pages/london/attractions'));
const Tube = lazy(() => import('../pages/london/tube'));
const LondonMap = lazy(() => import('../pages/london/londonmap'));
const LondonNight = lazy(() => import('../pages/london/londonnightlife'));
const LondonOdysseusChat = lazy(() => import('../pages/london/londonodysseus'));
const LondonStory = lazy(() => import('../pages/london/londonstory'));
const LondonTea = lazy(() => import('../pages/london/tea'));
const SundayRoast = lazy(() => import('../pages/london/sundayroast'));
const LondonBudget = lazy(() => import('../pages/london/londonbudget'));
const LondonFamilyCorner = lazy(() => import('../pages/london/londonfamilycorner'));
const LondonGreenspace = lazy(() => import('../pages/london/greenspaces'));
const LondonSafety = lazy(() => import('../pages/london/safetyandscams'));
const LondonHiddenGems = lazy(() => import('../pages/london/hiddengems'));
const LondonShopping = lazy(() => import('../pages/london/shopping'));

//Athens
const Athens = lazy(() => import('../pages/athens/athens'));

// Legal Pages
const PrivacyPolicy = lazy(() => import('../pages/legal/privacy'));
const TermsOfService = lazy(() => import('../pages/legal/termsofservice'));

export const CITIES = {
  PARIS: 'paris',
  ITALY: 'italy',
  LONDON: 'london',
  ATHENS: 'athens'
};

export const cityIcons = {
  [CITIES.PARIS]: mdiEiffelTower,
  [CITIES.LONDON]: mdiFerrisWheel,
  [CITIES.ATHENS]: mdiBankOutline,
  [CITIES.ITALY]: mdiBridge
};

export const routeTypes = {
  PUBLIC: 'public',
  AUTH: 'auth',
  PREMIUM: 'premium',
  LEGAL: 'legal'
};

export const routes = {
  // Navigation routes
  nav: {
    home: {
      path: '/',
      text: 'Home',
      icon: Home,
      component: Landing,
      type: routeTypes.PUBLIC
    },
    dashboard: {
      path: '/app/checkout',
      text: "Expert Access",
      icon: ShoppingCart,
      component: Checkout,
      type: routeTypes.AUTH
    },
    profile: {
      path: '/app/profile',
      text: 'Profile',
      icon: UserCircle,
      component: UserProfile,
      type: routeTypes.AUTH
    },
    auth: {
      path: '/auth',
      text: 'Sign In',
      icon: LogIn,
      component: AuthComponent,
      type: routeTypes.PUBLIC
    },
  },

  // City routes
  cities: {
    [CITIES.PARIS]: {
      overview: {
        path: '/paris',
        text: 'Paris Overview',
        component: Paris,
        type: routeTypes.PUBLIC
      },
      parisstays: {
        path: '/parisstays',
        text: 'Paris Location Recommendations',
        component: ParisStay,
        type: routeTypes.PUBLIC
      },
      pack: {
        path: '/parispack',
        text: 'Odysseus Packing Suggestions',
        component: ParisPack,
        type: routeTypes.PUBLIC
      },
      architecture: {
        path: '/architecture',
        text: 'Architecture',
        component: ParisArchitecture,
        type: routeTypes.PUBLIC
      },
      metro: {
        path: '/metro',
        text: 'Metro',
        component: Metro,
        type: routeTypes.PUBLIC
      },
      parismap: {
        path: '/parismap',
        text: 'Map of Paris',
        component: ParisMap,
        type: routeTypes.PUBLIC
      },
      monuments: {
        path: '/monuments',
        text: 'Monuments',
        component: Monuments,
        type: routeTypes.PUBLIC
      },
      museums: {
        path: '/museums',
        text: 'Museums',
        component: Museums,
        type: routeTypes.PUBLIC
      },
      nightlife: {
        path: '/parisnightlife',
        text: 'Night Life',
        component: ParisNightlife,
        type: routeTypes.PUBLIC
      },
      emergency: {
        path: '/emergencyinfo',
        text: 'Safey and Scams',
        component: EmergencyInfo,
        type: routeTypes.PUBLIC
      },
      // Premium routes
      assistant: {
        path: '/app/odyessus',
        text: 'Odyessus Travel Assistant',
        component: OdysseusPage,
        type: routeTypes.PREMIUM
      },
      liveUpdates: {
        path: '/app/live',
        text: 'Odyessus Daily Updates',
        component: LiveUpdates,
        type: routeTypes.PREMIUM
      },
      story: {
        path: '/app/parisstory',
        text: 'Odysseus 3D Story',
        component: ThreeStory,
        type: routeTypes.PREMIUM
      },
      dining: {
        path: '/app/dining',
        text: 'Favorite Restaurants',
        component: RestaurantPage,
        type: routeTypes.PREMIUM
      },
      bakeries: {
        path: '/app/boulangeries',
        text: 'Best Boulangeries',
        component: Bakeries,
        type: routeTypes.PREMIUM
      },
      culture: {
        path: '/app/culture',
        text: 'Culture and Etiquette',
        component: CulturalEtiquette,
        type: routeTypes.PREMIUM
      },
      familyCorner: {
        path: '/app/familycorner',
        text: 'Family Corner',
        component: FamilyCorner,
        type: routeTypes.PREMIUM
      },
      greenSpaces: {
        path: '/app/greenspaces',
        text: 'Amazing Green Spaces',
        component: GreenSpaces,
        type: routeTypes.PREMIUM
      },
      shopping: {
        path: '/app/shopping',
        text: 'Chic Shopping Districts',
        component: ShoppingDistricts,
        type: routeTypes.PREMIUM
      },
      parisbudget: {
        path: '/app/parisbudget',
        text: 'Paris Budget',
        component: ParisBudget,
        type: routeTypes.PREMIUM
      },
      parishiddengems: {
        path: '/app/parishiddengems',
        text: 'Paris Hidden Gems',
        component: ParisHiddenGems,
        type: routeTypes.PREMIUM
      }
    },

    [CITIES.ITALY]: {
      overview: {
        path: '/italy',
        text: 'Italy Overview',
        component: Italy,
        type: routeTypes.PUBLIC
      },
      emergency: {
        path: '/italy/emergencyinfo',
        text: 'Italy Safety and Scams',
        component: ItalySaftey,
        type: routeTypes.PUBLIC
      },
      travel: {
        path: '/app/italy/italytravel',
        text: 'Italy - Getting Around',
        component: TravelingInItaly,
        type: routeTypes.PREMIUM
      },
      cafe: {
        path: '/app/italy/cafe',
        text: 'Italy - Cafe Culture',
        component: ItalianCafeGuide,
        type: routeTypes.PREMIUM
      },
      rome: {
        path: '/italy/rome',
        text: 'Rome Overview',
        component: Rome,
        type: routeTypes.PUBLIC
      },
      romestays: {
        path: '/italy/romestays',
        text: 'Rome Location Recommendations',
        component: RomeStay,
        type: routeTypes.PUBLIC
      },
      romepack: {
        path: '/italy/romepack',
        text: 'Rome Odysseus Packing Suggesstions',
        component: RomePack,
        type: routeTypes.PUBLIC
      },
      romemap: {
        path: '/italy/romemap',
        text: 'Rome - Map',
        component: RomeMap,
        type: routeTypes.PUBLIC
      },
      romeattractions: {
        path: '/italy/romeattractions',
        text: 'Rome Ancient Sites',
        component: RomeAttractions,
        type: routeTypes.PUBLIC
      },
      romemodern: {
        path: '/italy/romemodernsites',
        text: 'Rome Modern Attractions',
        component: RomeModern,
        type: routeTypes.PUBLIC
      },
      romenightlife: {
        path: '/italy/romenightlife',
        text: 'Rome Nightlife',
        component: RomeNightLife,
        type: routeTypes.PUBLIC
      },
      romejubilee: {
        path: '/app/italy/jubilee',
        text: 'Rome Jubilee',
        component: RomeJubilee,
        type: routeTypes.PREMIUM
      },
      romechat: {
        path: '/app/italy/romeodysseus',
        text: 'Rome Odysseus Travel Advisor',
        component: RomeOdysseusChat,
        type: routeTypes.PREMIUM
      },
      romestory: {
        path: '/app/italy/romestory',
        text: 'Rome 3D Story',
        component: RomeStory,
        type: routeTypes.PREMIUM
      },
      romelive: {
        path: '/app/italy/romelive',
        text: 'Rome Odysseus Live Feed',
        component: RomeLive,
        type: routeTypes.PREMIUM
      },
      romepizza: {
        path: '/app/italy/romepizza',
        text: 'Rome Best Pizza',
        component: RomePizza,
        type: routeTypes.PREMIUM
      },
      romefood: {
        path: '/app/italy/romefood',
        text: 'Rome Best Food',
        component: RomeFood,
        type: routeTypes.PREMIUM
      },
      romebudget: {
        path: '/app/italy/romebudget',
        text: 'Rome Budget Guide',
        component: RomeBudget,
        type: routeTypes.PREMIUM
      },
      romehiddengems: {
        path: '/app/italy/romehiddengems',
        text: 'Rome Hidden Gems',
        component: RomeHiddenGems,
        type: routeTypes.PREMIUM
      },
      romefamilycorner: {
        path: '/app/italy/romefamilycorner',
        text: 'Rome Family Corner',
        component: RomeFamilyCorner,
        type: routeTypes.PREMIUM
      },
      romeshopping: {
        path: '/app/italy/romeshopping',
        text: 'Rome Shopping',
        component: RomeShopping,
        type: routeTypes.PREMIUM
      },
      florence: {
        path: '/italy/florence',
        text: 'Florence Overview',
        component: Florence,
        type: routeTypes.PUBLIC
      },
      florencemap: {
        path: '/italy/florencemap',
        text: 'Florence - Map',
        component: FlorenceMap,
        type: routeTypes.PUBLIC
      },
      florenceattractions: {
        path: '/italy/florenceattractions',
        text: 'Florence Attractions',
        component: FlorenceAttractions,
        type: routeTypes.PUBLIC
      },
      florencenightlife: {
        path: '/florencenightlife',
        text: 'Florence Night Life',
        component: FlorenceNightLife,
        type: routeTypes.PUBLIC
      },
      florenceodysseus: {
        path: '/app/italy/florenceodysseus',
        text: 'Florence Odysseus Travel Advisor',
        component: FlorenceOdysseusChat,
        type: routeTypes.PREMIUM
      },
      florencelive: {
        path: '/app/italy/florencelive',
        text: 'Florence Odysseus Live Feed',
        component: FlorenceLive,
        type: routeTypes.PREMIUM
      },
      florencestory: {
        path: '/app/italy/florencestory',
        text: 'Florence 3D Story',
        component: FlorenceStory,
        type: routeTypes.PREMIUM
      },
      florencehiddengems: {
        path: '/app/italy/florencehiddengems',
        text: 'Florence Hidden Gems',
        component: FlorenceHiddenGems,
        type: routeTypes.PREMIUM
      },
      florencebudget: {
        path: '/app/italy/florencebudget',
        text: 'Florence Budget Guide',
        component: FlorenceBudget,
        type: routeTypes.PREMIUM
      },
      florencekids: {
        path: '/app/italy/florencefamilycorner',
        text: 'Florence Family Corner',
        component: FlorenceFamily,
        type: routeTypes.PREMIUM
      },
      florencesteak: {
        path: '/app/italy/florencesteak',
        text: 'Bistecca alla Fiorentina',
        component: FlorenceSteak,
        type: routeTypes.PREMIUM
      },
      florenceshopping: {
        path: '/app/italy/florenceshopping',
        text: 'Florence Shopping',
        component: FlorenceShopping,
        type: routeTypes.PREMIUM
      },
      milan: {
        path: '/italy/milan',
        text: 'Milan',
        component: Milan,
        type: routeTypes.PUBLIC
      },
      milanmap: {
        path: '/italy/milanmaps',
        text: 'Milan - Map',
        component: MilanMap,
        type: routeTypes.PUBLIC
      },
      milanattractions: {
        path: '/italy/milanattractions',
        text: 'Milan Attractions',
        component: MilanAttractions,
        type: routeTypes.PUBLIC
      },
      milanlife: {
        path: '/milannightlife',
        text: 'Milan Night Life',
        component: MilanNightLife,
        type: routeTypes.PUBLIC
      },
      milanodysseus: {
        path: '/app/italy/milanodysseus',
        text: 'Milan Odysseus Travel Advisor',
        component: MilanOdysseusChat,
        type: routeTypes.PREMIUM
      },
      milanlive: {
        path: '/app/italy/milanlive',
        text: 'Milan Odysseus Live Feed',
        component: MilanLive,
        type: routeTypes.PREMIUM
      },
      milanstory: {
        path: '/app/italy/milanstory',
        text: 'Milan 3D Story',
        component: MilanStory,
        type: routeTypes.PREMIUM
      },
      milanfood: {
        path: '/app/italy/milanfood',
        text: 'Milan Food Guide',
        component: MilanFood,
        type: routeTypes.PREMIUM
      },
      MilanShopping: {
        path: '/app/italy/milanshopping',
        text: 'Milan Shopping',
        component: MilanShopping,
        type: routeTypes.PREMIUM
      },
      MilanHiddenGems: {
        path: '/app/italy/milanhiddengems',
        text: 'Milan Hidden Gems',
        component: MilanHiddenGems,
        type: routeTypes.PREMIUM
      },
      milanbudget: {
        path: '/app/italy/milanbudget',
        text: 'Milan Budget Guide',
        component: MilanBudget,
        type: routeTypes.PREMIUM
      },
      venicemap: {
        path: '/italy/venicemap',
        text: 'Vence - Map',
        component: VeniceMap,
        type: routeTypes.PUBLIC
      },
      naplesmap: {
        path: '/italy/naplesmaps',
        text: 'Naples - Map',
        component: NaplesMap,
        type: routeTypes.PUBLIC
      }
    },

    [CITIES.LONDON]: {
      overview: {
        path: '/london',
        text: 'London Overview',
        component: London,
        type: routeTypes.PUBLIC
      },
      londonmap: {
        path: '/london/londonmap',
        text: 'Map of London',
        component: LondonMap,
        type: routeTypes.PUBLIC
      },
      tube: {
        path: '/london/tube',
        text: 'The Tube',
        component: Tube,
        type: routeTypes.PUBLIC
      },
      attractions: {
        path: '/london/attractions',
        text: 'Attractions',
        component: LondonAttractions,
        type: routeTypes.PUBLIC
      },
      londonmonuments: {
        path: '/london/monuments',
        text: 'Monuments',
        component: LondonMonuments,
        type: routeTypes.PUBLIC
      },
      londonsafety: {
        path: '/london/safetyandscams',
        text: 'Safety and Scams',
        component: LondonSafety,
        type: routeTypes.PUBLIC
      },
      londonnightlife: {
        path: '/london/nightlife',
        text: 'Night Life',
        component: LondonNight,
        type: routeTypes.PUBLIC
      },
      londonodysseus: {
        path: '/app/london/londonodysseus',
        text: 'London Odysseus Travel Advisor',
        component: LondonOdysseusChat,
        type: routeTypes.PREMIUM
      },
      londonstory: {
        path: '/app/london/londonstory',
        text: 'London 3D Story',
        component: LondonStory,
        type: routeTypes.PREMIUM
      },
      londontea: {
        path: '/app/london/afternoontea',
        text: 'Afternoon Tea',
        component: LondonTea,
        type: routeTypes.PREMIUM
      },
      londonsunday: {
        path: '/app/london/sunday',
        text: 'Sunday Roast',
        component: SundayRoast,
        type: routeTypes.PREMIUM
      },
      londongreenspace: {
        path: '/app/london/greenspace',
        text: 'Greenspace',
        component: LondonGreenspace,
        type: routeTypes.PREMIUM
      },
      londonbudget: {
        path: '/app/london/londonbudget',
        text: 'Budget Guide',
        component: LondonBudget,
        type: routeTypes.PREMIUM
      },
      londonfamilycorner: {
      path: '/app/london/londonfamilycorner',
      text: 'Family Corner',
      component: LondonFamilyCorner,
      type: routeTypes.PREMIUM
      },
      londonbudget: {
        path: '/app/london/hiddengems',
        text: 'Hidden Gems',
        component: LondonHiddenGems,
        type: routeTypes.PREMIUM
      },
      londonshopping: {
        path: '/app/london/shopping',
        text: 'Shopping',
        component: LondonShopping,
        type: routeTypes.PREMIUM
      },
  },

    [CITIES.ATHENS]: {
      overview: {
        path: '/athens',
        text: 'Athens Overview',
        component: Athens,
        type: routeTypes.PUBLIC
      }
    }
  },

  // Legal routes
  legal: {
    privacy: {
      path: '/legal/privacy',
      text: 'Privacy Policy',
      component: PrivacyPolicy,
      type: routeTypes.LEGAL
    },
    terms: {
      path: '/legal/termsofservice',
      text: 'Terms of Service',
      component: TermsOfService,
      type: routeTypes.LEGAL
    }
  },
  hidden: {  // Changed from hiddenPage to hidden for consistency
    parislanding: {
      path: '/paris-explore',  // Changed path to be more SEO-friendly
      text: 'Paris Landing Page',
      component: ParisLanding,
      type: routeTypes.PUBLIC
    },
    romelanding: {
      path: '/rome-explore',   // Changed path to be more SEO-friendly
      text: 'Rome Landing Page',
      component: RomeLanding,
      type: routeTypes.PUBLIC
    },
    romelandingb: {
      path: '/rome-explore2',   // Changed path to be more SEO-friendly
      text: 'Rome Landing Page',
      component: RomeLanding2,
      type: routeTypes.PUBLIC
    },
    parislandingb: {
      path: '/paris-explore2',   // Changed path to be more SEO-friendly
      text: 'Paris Landing Page',
      component: ParisLanding2,
      type: routeTypes.PUBLIC
    },
    italylanding: {
      path: '/italy-explore',   // Changed path to be more SEO-friendly
      text: 'Italy Landing Page',
      component: ItalyLanding,
      type: routeTypes.PUBLIC
    },
    intinerarybuilder: {
      path: '/app/itinerarybuilder', 
      text: 'Builder',
      component: ParisItineraryBuilder,
      type: routeTypes.PUBLIC
    },
    checkoutsuccess: {
      path: '/app/CheckoutSuccess', 
      text: 'Checkout Success',
      component: CheckoutSuccess,
      type: routeTypes.PUBLIC
    },
    translate: {
      path: '/app/translator', 
      text: 'translate',
      component: Translator,
      type: routeTypes.PUBLIC
    },
  }
};


// Helper functions
export const getCityRoutes = (cityKey) => {
  return routes.cities[cityKey] || {};
};

export const getRoutesByType = (type) => {
  const allRoutes = [
    ...Object.values(routes.nav),
    ...Object.values(routes.cities).flatMap(city => Object.values(city)),
    ...Object.values(routes.legal),
    ...Object.values(routes.hidden) 
  ];
  return allRoutes.filter(route => route.type === type);
};

export const getPublicRoutes = () => getRoutesByType(routeTypes.PUBLIC);
export const getAuthRoutes = () => getRoutesByType(routeTypes.AUTH);
export const getPremiumRoutes = () => getRoutesByType(routeTypes.PREMIUM);
export const getLegalRoutes = () => getRoutesByType(routeTypes.LEGAL);

export const getAllRoutes = () => ({
  public: getPublicRoutes(),
  auth: getAuthRoutes(),
  premium: getPremiumRoutes(),
  legal: getLegalRoutes()
});

export default routes;